import { useQuery } from "@tanstack/react-query";

import { payrollApi } from "@/lib/api/payroll";
import { qk } from "@/lib/query-client";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { StatTile } from "@/components/ui/StatTile";
import { StatusBadge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { EmptyState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { formatMoney, formatPeriod } from "@/lib/utils/format";

function periodLabel(year: number, month: number): string {
  return formatPeriod(`${year}-${String(month).padStart(2, "0")}`);
}

/** The Payroll landing page: every figure comes straight off the existing
 * payroll/runs list (each run already carries employee_count/processed_count/
 * failed_count/total_gross/total_net/total_ctc) - no new backend work, same
 * disclosed "frontend-only" dashboard as EmployeeManagementDashboardPage/
 * LeaveDashboardPage. No exception/anomaly detection beyond a run's own
 * failed_count - a richer exceptions view would need new backend aggregation,
 * deliberately not built here. */
export function PayrollDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: qk.payroll.runs({ per_page: 12 }),
    queryFn: () => payrollApi.runs({ per_page: 12 }),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-6 w-6 text-primary-600" />
      </div>
    );
  }
  if (error || !data) return <ErrorMessage error={error} />;

  const runs = data.items;
  const latest = runs[0];
  const maxGross = Math.max(1, ...runs.map((r) => Number(r.total_gross)));

  return (
    <div>
      <PageHeader title="Payroll Dashboard" description="Latest run status, recent trend and payroll history at a glance." />

      {!latest ? (
        <EmptyState icon={<Icon name="wallet" className="h-8 w-8" />} title="No payroll runs yet" description="Generate your first payroll run to see figures here." />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatTile label="Latest Period" value={periodLabel(latest.period_year, latest.period_month)} hint={latest.status} />
            <StatTile label="Employees" value={latest.employee_count} hint={`${latest.processed_count} processed`} />
            <StatTile label="Failed" value={latest.failed_count} />
            <StatTile label="Gross" value={formatMoney(latest.total_gross)} emphasis />
            <StatTile label="Deductions" value={formatMoney(latest.total_deductions)} />
            <StatTile label="Net Pay" value={formatMoney(latest.total_net)} />
            <StatTile label="Employer PF" value={formatMoney(latest.total_employer_pf)} />
            <StatTile label="Total CTC" value={formatMoney(latest.total_ctc)} />
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Gross Pay Trend</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                {[...runs].reverse().map((r) => (
                  <div key={r.id}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-700">{periodLabel(r.period_year, r.period_month)}</span>
                      <span className="text-slate-500">{formatMoney(r.total_gross)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-primary-600" style={{ width: `${(Number(r.total_gross) / maxGross) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Recent Runs</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2 font-medium">Period</th>
                    <th className="px-5 py-2 font-medium">Status</th>
                    <th className="px-5 py-2 font-medium">Employees</th>
                    <th className="px-5 py-2 text-right font-medium">Net Pay</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runs.map((r) => (
                    <tr key={r.id} className="transition-colors duration-200 hover:bg-primary-50/40">
                      <td className="px-5 py-2.5 text-slate-800">{periodLabel(r.period_year, r.period_month)}</td>
                      <td className="px-5 py-2.5">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-5 py-2.5 text-slate-600">{r.employee_count}</td>
                      <td className="px-5 py-2.5 text-right font-medium text-slate-800">{formatMoney(r.total_net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
